import React, { useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getDramaRevenue } from "@/services/firebase/revenue";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import FinancialDateRangePicker from "@/components/financial/FinancialDateRangePicker";
import { useFinancialDateRange } from "@/store/financialDateRange";

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export default function DramaRevenuePage() {
  const { dramaId } = useParams<{ dramaId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { start, end, setRange } = useFinancialDateRange();

  // mount：URL 帶 start/end → 套用到 store
  useEffect(() => {
    const urlStart = searchParams.get("start");
    const urlEnd = searchParams.get("end");
    if (
      urlStart && urlEnd &&
      DATE_RE.test(urlStart) && DATE_RE.test(urlEnd) &&
      (urlStart !== start || urlEnd !== end)
    ) {
      setRange(urlStart, urlEnd);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // store 變動 → 同步寫回 URL（保留分享連結 & 重新整理還原）
  useEffect(() => {
    if (searchParams.get("start") !== start || searchParams.get("end") !== end) {
      const next = new URLSearchParams(searchParams);
      next.set("start", start);
      next.set("end", end);
      setSearchParams(next, { replace: true });
    }
  }, [start, end, searchParams, setSearchParams]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["drama-revenue", dramaId, start, end],
    queryFn: () => getDramaRevenue(dramaId!, { startDate: start, endDate: end }),
    enabled: !!dramaId && !!start && !!end,
  });

  // 全劇購買沒有集數：新版 API 回傳 full_drama；舊版把它算成第 0 集
  const rawEpisodes: any[] = data?.episodes || [];
  const episodes = rawEpisodes.filter((e) => e.episode > 0).sort((a, b) => a.episode - b.episode);
  const fullDrama = data?.full_drama ?? rawEpisodes.find((e) => e.episode === 0) ?? null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-700">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-surface-900 dark:text-white">
              {data?.drama_title
                ? `劇集營收詳情 — ${data.drama_title}`
                : "劇集營收詳情"}
            </h1>
            <p className="text-sm text-surface-500 mt-1">Drama ID: {dramaId}</p>
          </div>
        </div>
        <FinancialDateRangePicker />
      </div>

      {isLoading && <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>}
      {error && <div className="p-4 bg-red-50 dark:bg-red-900/20 text-red-700 rounded-lg">載入失敗: {(error as Error).message}</div>}

      {data && (
        <>
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-white dark:bg-surface-800 rounded-xl p-5 shadow-sm border border-surface-200 dark:border-surface-700">
              <p className="text-xs text-surface-500 uppercase">付費金幣</p>
              <p className="mt-2 text-2xl font-bold text-surface-900 dark:text-white">{(data.total_paid_coins || 0).toLocaleString()}</p>
            </div>
            <div className="bg-white dark:bg-surface-800 rounded-xl p-5 shadow-sm border border-surface-200 dark:border-surface-700">
              <p className="text-xs text-surface-500 uppercase">免費金幣</p>
              <p className="mt-2 text-2xl font-bold text-surface-900 dark:text-white">{(data.total_free_coins || 0).toLocaleString()}</p>
            </div>
            <div className="bg-white dark:bg-surface-800 rounded-xl p-5 shadow-sm border border-surface-200 dark:border-surface-700">
              <p className="text-xs text-surface-500 uppercase">交易筆數</p>
              <p className="mt-2 text-2xl font-bold text-surface-900 dark:text-white">{(data.transaction_count || 0).toLocaleString()}</p>
            </div>
            <div className="bg-white dark:bg-surface-800 rounded-xl p-5 shadow-sm border border-surface-200 dark:border-surface-700">
              <p className="text-xs text-surface-500 uppercase">不重複用戶</p>
              <p className="mt-2 text-2xl font-bold text-surface-900 dark:text-white">{(data.unique_users || 0).toLocaleString()}</p>
            </div>
          </div>

          {episodes.length > 0 && (
            <div className="bg-white dark:bg-surface-800 rounded-xl p-6 shadow-sm border border-surface-200 dark:border-surface-700">
              <h3 className="text-sm font-semibold text-surface-700 dark:text-surface-300 mb-4">各集解鎖統計</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={episodes}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="episode" tick={{ fontSize: 11 }} label={{ value: "集數", position: "insideBottom", offset: -5 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="paid_coins" name="付費金幣" fill="#b45309" />
                  <Bar dataKey="free_coins" name="免費金幣" fill="#a8a29e" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="bg-white dark:bg-surface-800 rounded-xl shadow-sm border border-surface-200 dark:border-surface-700">
            <div className="px-6 py-4 border-b border-surface-200 dark:border-surface-700">
              <h3 className="text-sm font-semibold text-surface-700 dark:text-surface-300">各集明細</h3>
              {fullDrama && <p className="text-xs text-surface-500 mt-1">全劇購買一次解鎖整部劇，沒有集數，另列一行；上方長條圖只含各集解鎖。</p>}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-surface-500 border-b border-surface-200 dark:border-surface-700">
                    <th className="px-6 py-3 font-medium">集數</th>
                    <th className="px-6 py-3 font-medium text-right">付費金幣</th>
                    <th className="px-6 py-3 font-medium text-right">免費金幣</th>
                    <th className="px-6 py-3 font-medium text-right">交易數</th>
                    <th className="px-6 py-3 font-medium text-right">不重複用戶</th>
                  </tr>
                </thead>
                <tbody>
                  {fullDrama && (
                    <tr className="border-b border-surface-100 dark:border-surface-700 bg-surface-50 dark:bg-surface-900/40">
                      <td className="px-6 py-3 font-medium">全劇購買</td>
                      <td className="px-6 py-3 text-right">{fullDrama.paid_coins.toLocaleString()}</td>
                      <td className="px-6 py-3 text-right text-surface-500">{fullDrama.free_coins.toLocaleString()}</td>
                      <td className="px-6 py-3 text-right text-surface-500">{fullDrama.transaction_count}</td>
                      <td className="px-6 py-3 text-right text-surface-500">{fullDrama.unique_users}</td>
                    </tr>
                  )}
                  {episodes.map((ep: any) => (
                    <tr key={ep.episode} className="border-b border-surface-100 dark:border-surface-700">
                      <td className="px-6 py-3 font-medium">第 {ep.episode} 集</td>
                      <td className="px-6 py-3 text-right">{ep.paid_coins.toLocaleString()}</td>
                      <td className="px-6 py-3 text-right text-surface-500">{ep.free_coins.toLocaleString()}</td>
                      <td className="px-6 py-3 text-right text-surface-500">{ep.transaction_count}</td>
                      <td className="px-6 py-3 text-right text-surface-500">{ep.unique_users}</td>
                    </tr>
                  ))}
                  {episodes.length === 0 && !fullDrama && (
                    <tr><td colSpan={5} className="px-6 py-12 text-center text-surface-400">暫無數據</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
